import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Filter, Plus, Clock, AlertCircle, ChevronRight, ChevronLeft, CheckCircle, RotateCcw, XCircle, Info, X
} from 'lucide-react';
import './Ticket.css'
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import TicketDetail from './TicketDetail';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

function useClickOutside(ref, callback) {
  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [ref, callback]);
}
const Ticket = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [centres, setCentres] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [showMobileFilterPanel, setShowMobileFilterPanel] = useState(false);
  const [users, setUsers] = useState([]);
  const { userData } = useUser();
  const userId = userData?.userId;
  const isSuperAdmin = Array.isArray(userData?.roles) &&
    userData.roles.some(r => r.toLowerCase() === 'super admin');
  const auditRedact = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const SENSITIVE_KEYS = ['phone', 'mobile', 'email', 'whatsAppUrl', 'whatsappUrl', 'upi', 'upiId', 'pan', 'gst', 'aadhaar', 'password'];
    const mask = (v) => (typeof v === 'string' && v.length ? '********' : v);
    const clone = Array.isArray(obj) ? [] : {};
    for (const [k, v] of Object.entries(obj)) {
      if (SENSITIVE_KEYS.includes(k)) {
        clone[k] = mask(v);
      } else if (k.toLowerCase().includes('url') && typeof v === 'string') {
        try {
          const u = new URL(v);
          clone[k] = `${u.origin}/********`;
        } catch {
          clone[k] = '********';
        }
      } else if (v && typeof v === 'object') {
        clone[k] = auditRedact(v);
      } else {
        clone[k] = v;
      }
    }
    return clone;
  };
  const fetchTickets = async () => {
    setLoading(true);
    let url = `${process.env.REACT_APP_API_FETCH_TICKET}?userId=${userId}`;

    if (!isSuperAdmin) {
      const ids = Array.isArray(userData.academyIds)
        ? userData.academyIds.join(',')
        : userData.academyId;
      url += `&academyIds=${ids}`;
    }
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tickets');
      const result = await response.json();

      let ticketArray = [];

      const myTickets = Array.isArray(result.data?.myTickets)
        ? result.data.myTickets.map(mt => ({ ...mt.ticket, isMyTicket: true }))
        : [];

      const allTickets = Array.isArray(result.data?.tickets)
        ? result.data.tickets.map(t => ({ ...t, isMyTicket: false }))
        : [];

      const combinedTickets = [...myTickets, ...allTickets];
      const uniqueTickets = combinedTickets.filter((ticket, index, self) =>
        index === self.findIndex(t => t.id === ticket.id)
      );

      const mappedTickets = uniqueTickets.map(ticket => ({
        ...ticket,
        status: ticket.status,
        id: ticket.id?.toString() ?? '',
        ticketId: `TK00${ticket.id}` || '',
        title: ticket.title ?? '',
        category: ticket.categoryName ?? '',
        subcategory: ticket.subCategoryName ?? '',
        priority: ticket.priority?.charAt(0) + ticket.priority.slice(1).toLowerCase(),
        centre: ticket.academyName ?? '',
        assignedTo: Array.isArray(ticket.assignedTos) && ticket.assignedTos.length > 0
          ? ticket.assignedTos.map(o => o.name).join(', ')
          : (Array.isArray(ticket.owners) && ticket.owners.length > 0
            ? ticket.owners.map(o => o.name).join(', ')
            : 'Unassigned'),
        createdBy: ticket.createdBy?.name ?? '',
        createdAt: ticket.createdAt,
        updatedAt: ticket.updatedAt,
        description: ticket.description ?? '',
        attachments: ticket.attachments ?? [],
        comments: ticket.comments?.map(c => ({
          id: c.id,
          author: c.employeeName ?? '',
          text: c.comment,
          timestamp: c.createdAt,
          attachments: Array.isArray(c.attachments)
            ? c.attachments.map(att => ({
              id: att.id,
              photoUrl: att.photoUrl,
              videoUrl: att.videoUrl,
            }))
            : [],
        })) ?? [],
        activity: ticket.activityLogs?.map(log => ({
          type: log.actionType,
          user: log.performedBy?.name ?? '',
          details: log.details,
          timestamp: log.createdAt,
        })) ?? [],
        isMyTicket: ticket.isMyTicket || false,
      }));

      setTickets(mappedTickets);
    } catch (error) {
      setTickets([]);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed to fetch tickets!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const usersResponse = await fetch(process.env.REACT_APP_API_EMPLOYEE_LIST, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const usersData = await usersResponse.json();
        if (usersData.success) {
          setUsers(usersData.data.employees.map(user => ({
            id: user.id,
            name: user.name
          })));
        }
        const response = await fetch(process.env.REACT_APP_API_TICKET_DROPDOWN, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const data = await response.json();

        if (data.success) {
          setCentres(Object.values(data.data.academies));
          setCategories(data.data.categories);
          setPriorities(['Low', 'Medium', 'High', 'Critical']);
          const subs = {};
          data.data.categories.forEach(cat => {
            subs[cat.name] = cat.subcategories.map(sub => sub.name);
          });
          setSubcategories(subs);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
    };
    fetchDropdownData();
  }, []);

  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    centre: '',
    status: ['OPEN'],
    category: '',
    subcategory: '',
    priority: '',
    search: '',
    assignedTo: ''
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState([]);

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'Critical': return 'ts-priority-critical';
      case 'High': return 'ts-priority-high';
      case 'Medium': return 'ts-priority-medium';
      case 'Low': return 'ts-priority-low';
      default: return 'ts-priority-default';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'OPEN': return 'ts-status-open';
      case 'PROGRESS': return 'ts-status-in-progress';
      case 'RESOLVED': return 'ts-status-resolved';
      case 'REOPENED': return 'ts-status-reopened';
      case 'CLOSED': return 'ts-status-closed';
      default: return 'ts-status-default';
    }
  };

  const getStatusIcon = (status) => {
    const iconClass = "ts-icon-small ts-icon-inline ts-icon-margin-right";
    switch (status) {
      case 'OPEN': return <AlertCircle className={iconClass} />;
      case 'PROGRESS': return <Clock className={iconClass} />;
      case 'RESOLVED': return <CheckCircle className={iconClass} />;
      case 'REOPENED': return <RotateCcw className={iconClass} />;
      case 'CLOSED': return <XCircle className={iconClass} />;
      default: return <AlertCircle className={iconClass} />;
    }
  };
  const updateTicketPriority = async (ticketIds, priority) => {
    const url = `${process.env.REACT_APP_API_TICKET_STATUS_UPDATE}?ids=${ticketIds.join(',')}&priority=${priority.toUpperCase()}&performedBy=${userId}`;
    const snapshot = tickets.filter(t => ticketIds.includes(t.id)).map(t => ({ id: t.id, ticketId: t.ticketId, title: t.title, status: t.status, priority: t.priority, assignedTo: t.assignedTo }));
    const oldPayload = { tickets: snapshot };
    const newPriority = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
    const newPayload = { tickets: snapshot.map(t => ({ ...t, priority: newPriority })) };
    try {
      const response = await fetch(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update ticket priority');
      }
      const json = await response.json();
      fireAudit({
        entity: 'ticket',
        affected_id: ticketIds.join(','),
        action: 'priority-update',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload: auditRedact(oldPayload),
        newPayload: auditRedact(newPayload),
        section: 'Ticket',
        subSection: 'Ticket List(priority)',
        extra: { page: 'Ticket' }
      });
      return json;
    } catch (error) {
      console.error('Error updating ticket priority:', error);
      throw error;
    }
  };

  const [tempFilters, setTempFilters] = useState({
    centre: '',
    category: '',
    subcategory: '',
    priority: ''
  });
  const hasOpenTickets = tickets.some(ticket => ticket.status === 'OPEN');

  const effectiveFilters = {
    ...filters,
    status: hasOpenTickets ? filters.status : []
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesCentre = !effectiveFilters.centre || ticket.centre === effectiveFilters.centre;
    const matchesStatus = effectiveFilters.status.length === 0 || effectiveFilters.status.includes(ticket.status);
    const matchesCategory = !effectiveFilters.category || ticket.category === effectiveFilters.category;
    const matchesSubcategory = !effectiveFilters.subcategory || ticket.subcategory === effectiveFilters.subcategory;
    const matchesPriority = !effectiveFilters.priority || ticket.priority === effectiveFilters.priority;
    const matchesSearch = !effectiveFilters.search ||
      ticket.title.toLowerCase().includes(effectiveFilters.search.toLowerCase()) ||
      ticket.description.toLowerCase().includes(effectiveFilters.search.toLowerCase()) ||
      ticket.id.toLowerCase().includes(effectiveFilters.search.toLowerCase());
    const matchesMyTickets = !effectiveFilters.assignedTo || (effectiveFilters.assignedTo === userId && ticket.isMyTicket);

    return matchesCentre && matchesStatus && matchesCategory &&
      matchesPriority && matchesSubcategory && matchesSearch && matchesMyTickets;
  }).sort((a, b) => {
    if (a.status === 'CLOSED' && b.status !== 'CLOSED') return 1;
    if (b.status === 'CLOSED' && a.status !== 'CLOSED') return -1;
    const priorityOrder = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  useEffect(() => {
    setSelectedTickets([]);
  }, [filters, tickets]);

  useEffect(() => {
    fetchTickets();
  }, [filters.assignedTo]);
  const deleteTickets = async (ticketIds) => {
    const url = `${process.env.REACT_APP_API_TICKET_DELETE}?ids=${ticketIds.join(',')}`;
    const snapshot = tickets.filter(t => ticketIds.includes(t.id)).map(t => ({ id: t.id, ticketId: t.ticketId, title: t.title, status: t.status, priority: t.priority, assignedTo: t.assignedTo }));
    const oldPayload = { tickets: snapshot };
    const newPayload = { tickets: snapshot.map(t => ({ ...t, deleted: true })) };
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        }
      });

      if (!response.ok) throw new Error('Failed to delete tickets');

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Ticket(s) deleted successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
      const json = await response.json();
      fireAudit({
        entity: 'ticket',
        affected_id: ticketIds.join(','),
        action: 'delete',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload: auditRedact(oldPayload),
        newPayload: auditRedact(newPayload),
        section: 'Ticket',
        subSection: 'Ticket List(delete)',
        extra: { page: 'Ticket' }
      });
      return json;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete ticket(s).',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500
      });
      throw error;
    }
  };

  const updateTicketStatus = async (ticketIds, status) => {
    const url = `${process.env.REACT_APP_API_TICKET_STATUS_UPDATE}?ids=${ticketIds.join(',')}&status=${status.toUpperCase()}&performedBy=${userId}`;
    const snapshot = tickets.filter(t => ticketIds.includes(t.id)).map(t => ({ id: t.id, ticketId: t.ticketId, title: t.title, status: t.status, priority: t.priority, assignedTo: t.assignedTo }));
    const oldPayload = { tickets: snapshot };
    const newPayload = { tickets: snapshot.map(t => ({ ...t, status: status.toUpperCase() })) };
    try {
      const response = await fetch(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update ticket status');
      }

      const json = await response.json();
      fireAudit({
        entity: 'ticket',
        affected_id: ticketIds.join(','),
        action: 'status-update',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload: auditRedact(oldPayload),
        newPayload: auditRedact(newPayload),
        section: 'Ticket',
        subSection: 'Ticket List(status)',
        extra: { page: 'Ticket' }
      });
      return json;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      throw error;
    }
  };


  const reassignTickets = async (ticketIds, assigneeId) => {
    const url = `${process.env.REACT_APP_API_TICKET_RESSIGN}?ticketIds=${ticketIds.join(',')}&assignToIds=${assigneeId}&assignById=${userId}`;
    const snapshot = tickets.filter(t => ticketIds.includes(t.id)).map(t => ({ id: t.id, ticketId: t.ticketId, title: t.title, status: t.status, priority: t.priority, assignedTo: t.assignedTo }));
    const idsList = (assigneeId || '').split(',').filter(Boolean);
    const names = idsList.map(id => users.find(u => String(u.id) === String(id))?.name || id).join(', ');
    const oldPayload = { tickets: snapshot };
    const newPayload = { tickets: snapshot.map(t => ({ ...t, assignedTo: names })) };
    try {
      const response = await fetch(
        url
        , {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

      if (!response.ok) {
        throw new Error('Failed to reassign tickets');
      }

      const json = await response.json();
      fireAudit({
        entity: 'ticket',
        affected_id: ticketIds.join(','),
        action: 'reassign',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload: auditRedact(oldPayload),
        newPayload: auditRedact(newPayload),
        section: 'Ticket',
        subSection: 'Ticket List(reassign)',
        extra: { page: 'Ticket' }
      });
      return json;
    } catch (error) {
      console.error('Error reassigning tickets:', error);
      throw error;
    }
  };

  const handleBulkAction = async () => {
    if (selectedTickets.length === 0) return;

    const snapshot = tickets.filter(t => selectedTickets.includes(t.id)).map(t => ({ id: t.id, ticketId: t.ticketId, title: t.title, status: t.status, priority: t.priority, assignedTo: t.assignedTo }));
    let previewOld = null;
    let previewNew = null;
    if (bulkAction === 'delete') {
      previewOld = { tickets: snapshot };
      previewNew = { tickets: snapshot.map(t => ({ ...t, deleted: true })) };
    } else if (bulkAction.startsWith('status-')) {
      const newStatus = bulkAction.split('-')[1];
      previewOld = { tickets: snapshot };
      previewNew = { tickets: snapshot.map(t => ({ ...t, status: newStatus })) };
    } else if (bulkAction === 'assign' && selectedAssignee.length) {
      const assigneeNames = selectedAssignee.map(u => u.name).join(', ');
      previewOld = { tickets: snapshot };
      previewNew = { tickets: snapshot.map(t => ({ ...t, assignedTo: assigneeNames })) };
    } else if (bulkAction.startsWith('priority-')) {
      const p = bulkAction.split('-')[1];
      const newPriority = p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      previewOld = { tickets: snapshot };
      previewNew = { tickets: snapshot.map(t => ({ ...t, priority: newPriority })) };
    }

    if (previewOld && previewNew) {
      const { html } = buildDiffPreview(auditRedact(previewOld), auditRedact(previewNew));
      const confirm = await Swal.fire({ title: 'Apply these changes?', html, showCancelButton: true, confirmButtonText: 'Apply' });
      if (!confirm.isConfirmed) return;
    }

    try {
      let result;
      let successTitle = "Success";
      let successText = "Bulk action completed successfully!";

      if (bulkAction === 'delete') {
        result = await deleteTickets(selectedTickets);
        successTitle = "Deleted!";
        successText = "Selected tickets have been deleted.";
      } else if (bulkAction.startsWith('status-')) {
        const newStatus = bulkAction.split('-')[1];
        result = await updateTicketStatus(selectedTickets, newStatus);
        successTitle = "Status Updated!";
        successText = "Ticket status has been updated successfully.";
      } else if (bulkAction === 'assign' && selectedAssignee.length) {
        const assigneeIds = selectedAssignee.map(u => u.id).join(',');
        result = await reassignTickets(selectedTickets, assigneeIds);
        successTitle = "Assigned!";
        successText = `Ticket(s) assigned to ${selectedAssignee.map(u => u.name).join(', ')} successfully.`;
      } else if (bulkAction.startsWith('priority-')) {
        const newPriority = bulkAction.split('-')[1];
        result = await updateTicketPriority(selectedTickets, newPriority);
        successTitle = "Priority Updated!";
        successText = `Ticket priority set to ${newPriority.charAt(0) + newPriority.slice(1).toLowerCase()} successfully.`;
      }

      if (result && result.success) {
        await fetchTickets();
        Swal.fire({
          icon: "success",
          title: successTitle,
          text: successText,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });
        setSelectedTickets([]);
        setBulkAction('');
        setSelectedAssignee(null);
        setShowAssignDropdown(false);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result?.success_message || "Failed to perform action. Please try again.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }

    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to perform bulk action. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage] = useState(10);

  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);


  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));


  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const UserAvatar = ({ name }) => {
    const getInitials = (fullName) => {
      if (!fullName || typeof fullName !== 'string') return '';
      const parts = fullName.split(' ');
      if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
      return (parts[0][0] || '') + (parts[parts.length - 1][0] || '');
    };


    const initials = getInitials(name);
    const bgColorClass = name === userId ? 'ts-avatar-bg-blue' : 'ts-avatar-bg-gray';

    return (
      <div className={`ts-user-avatar ${bgColorClass}`}>
        {initials.toUpperCase()}
      </div>
    );
  };

  const centreOptions = centres.map(centre => ({ value: centre, label: centre }));
  const categoryOptions = categories.map(cat => ({ value: cat.name, label: cat.name }));
  const priorityOptions = priorities.map(priority => ({ value: priority, label: priority }));

  const getBulkStatusOptions = () => {
    if (!filters.category) return [];
    const selectedCat = categories.find(cat => cat.name === filters.category);
    if (!selectedCat || !Array.isArray(selectedCat.statuses)) return [];
    return selectedCat.statuses.map(obj => {
      const [id, label] = Object.entries(obj)[0];
      return { value: `status-${label.toUpperCase()}`, label: `Set to ${label}` };
    });
  };

  const getSubcategoryOptions = () => {
    if (!filters.category) return [];
    return subcategories[filters.category]?.map(subcat => ({
      value: subcat,
      label: subcat
    })) || [];
  };
  const getSubcategoryOptionsPhone = (category) => {
    if (!category) return [];
    return subcategories[category]?.map(subcat => ({
      value: subcat,
      label: subcat
    })) || [];
  };
  const bulkActionOptions = [
    ...(hasPrivilege(userData, 'TICKET', 'TICKETS_DELETE')
      ? [{ value: 'delete', label: 'Disable Selected' }]
      : []),

    ...(hasPrivilege(userData, 'TICKET', 'TICKETS_STATUS_MODIFY') && filters.category && getBulkStatusOptions().length > 0
      ? [{
        label: 'Change Status',
        options: getBulkStatusOptions(),
      }]
      : []),

    ...(hasPrivilege(userData, 'TICKET', 'TICKETS_PRIORITY_MODIFY') && priorities.length > 0
      ? [{
        label: 'Change Priority',
        options: priorities.map(p => ({
          value: `priority-${p.toLowerCase()}`,
          label: `Set to ${p}`
        }))
      }]
      : []),

    ...(hasPrivilege(userData, 'TICKET', 'TICKETS_ASSIGN')
      ? [{ value: 'assign', label: 'Assign' }]
      : []),
  ];


  const handleFilterChange = (key, selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'category' ? { subcategory: '' } : {})
    }));
  };

  const handleBulkActionChange = (selectedOption) => {
    if (selectedOption) {
      setBulkAction(selectedOption.value);
      if (selectedOption.value === 'assign') {
        setShowAssignDropdown(true);
      } else {
        setShowAssignDropdown(false);
        setSelectedAssignee([]);
      }
    } else {
      setBulkAction('');
      setShowAssignDropdown(false);
      setSelectedAssignee([]);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    if (selectedTicket && tickets.length > 0) {
      const latest = tickets.find(t => t.id === selectedTicket.id);
      if (latest && JSON.stringify(latest) !== JSON.stringify(selectedTicket)) {
        setSelectedTicket(latest);
      }
    }
  }, [tickets]);
  const mobileDetailRef = useRef();

  useClickOutside(mobileDetailRef, () => {
    if (selectedTicket && window.innerWidth < 768) setSelectedTicket(null);
  });
  const hasAnyBulkPrivilege =
    hasPrivilege(userData, 'TICKET', 'TICKETS_DELETE') ||
    hasPrivilege(userData, 'TICKET', 'TICKETS_STATUS_MODIFY') ||
    hasPrivilege(userData, 'TICKET', 'TICKETS_PRIORITY_MODIFY') ||
    hasPrivilege(userData, 'TICKET', 'TICKETS_ASSIGN');

  const handleNewTicket = (event) => {
    const url = `/newticket`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  return (
    <div className="enrollment-container" style={{ display: window.innerWidth < 768 ? 'block' : 'flex', padding: '2px', overflow: 'visible' }}>
      <aside className='ts-sidebar-container' style={{ flex: '0 45%' }}>
        <div className="ts-sidebar-header">
          <h2 className="ts-sidebar-title">Tickets</h2>
          <button onClick={handleNewTicket} className="ts-sidebar-create-button">
            <Plus className="ts-icon-small" /> Create Ticket
          </button>
        </div>

        <div className="ts-filters-section">
          <div className="ts-search-and-filter-row">
            <div className="ts-search-input-container">
              <Search className="ts-search-icon" />
              <input
                type="text"
                placeholder="Search tickets by title or ID..."
                className="ts-search-input"
                value={filters.search}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    search: e.target.value
                  }))
                }
              />
            </div>
            {window.innerWidth < 768 ? (
              <button
                onClick={() => {
                  setTempFilters({
                    centre: filters.centre,
                    category: filters.category,
                    subcategory: filters.subcategory,
                    priority: filters.priority
                  });
                  setShowMobileFilterPanel(true);
                }}
                className="ts-filter-toggle-button"
              >
                <Filter className="ts-icon-small" />
              </button>
            ) : (
              <button
                onClick={() => setShowFilters(prev => !prev)}
                className="ts-filter-toggle-button"
              >
                <Filter className="ts-icon-small" />
              </button>
            )}
          </div>

          <div className="ts-filter-buttons-container">
            <button
              onClick={() => {
                setFilters(prev => ({
                  ...prev,
                  assignedTo: prev.assignedTo === userId ? '' : userId
                }));
              }}
              className={`ts-filter-button ${filters.assignedTo === userId ? 'ts-filter-button-active' : 'ts-filter-button-inactive'}`}
            >
              My Tickets
            </button>

            {Array.from(new Set(tickets.map(ticket => ticket.status))).map(status => (
              <button
                key={status}
                onClick={() => {
                  setFilters(prev => {
                    const statusArray = prev.status || [];
                    if (statusArray.includes(status)) {
                      return { ...prev, status: statusArray.filter(s => s !== status) };
                    } else {
                      return { ...prev, status: [...statusArray, status] };
                    }
                  });
                }}
                className={`ts-filter-button ${(filters.status || []).includes(status)
                  ? 'ts-filter-button-active'
                  : 'ts-filter-button-inactive'
                  }`}
              >
                {status}
              </button>
            ))}
          </div>
          {window.innerWidth < 768 && showMobileFilterPanel && (
            <div className="ts-mobile-filter-overlay">
              <div className="ts-mobile-filter-panel">
                <div className="ts-mobile-filter-header">
                  <h3>Filter Tickets</h3>
                  <button onClick={() => setShowMobileFilterPanel(false)}>Close</button>
                </div>

                <div className="ts-mobile-filter-body">
                  <div className="floating-select-form" style={{ width: '100%' }}>
                    <Select
                      className={`walkin-form-dropdown ${tempFilters.centre ? "has-value" : ""}`}
                      classNamePrefix="ts-react-select"
                      options={centreOptions}
                      placeholder="Centre"
                      isClearable
                      value={tempFilters.centre ? centreOptions.find(opt => opt.value === tempFilters.centre) : null}
                      onChange={(selected) => setTempFilters(prev => ({
                        ...prev,
                        centre: selected ? selected.value : '',
                        ...(selected ? {} : { subcategory: '' })
                      }))}
                      menuPortalTarget={document.body}
                      styles={{
                        placeholder: (provided) => ({
                          ...provided,
                          color: 'black',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {tempFilters.centre && <label htmlFor="tier-select">Centre</label>}
                  </div>
                  <div className="floating-select-form" style={{ width: '100%' }}>
                    <Select
                      className={`walkin-form-dropdown ${tempFilters.category ? "has-value" : ""}`}
                      classNamePrefix="ts-react-select"
                      options={categoryOptions}
                      placeholder="Category"
                      isClearable
                      value={tempFilters.category ? categoryOptions.find(opt => opt.value === tempFilters.category) : null}
                      onChange={(selected) => setTempFilters(prev => ({
                        ...prev,
                        category: selected ? selected.value : '',
                        subcategory: ''
                      }))}
                      menuPortalTarget={document.body}
                      styles={{
                        placeholder: (provided) => ({
                          ...provided,
                          color: 'black',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {tempFilters.category && <label htmlFor="tier-select">Category</label>}
                  </div>
                  <div className="floating-select-form" style={{ width: '100%' }}>
                    <Select
                      className={`walkin-form-dropdown ${tempFilters.subcategory ? "has-value" : ""}`}
                      classNamePrefix="ts-react-select"
                      options={getSubcategoryOptionsPhone(tempFilters.category)}
                      placeholder="Subcategory"
                      isClearable
                      isDisabled={!tempFilters.category}
                      value={tempFilters.subcategory ? getSubcategoryOptionsPhone(tempFilters.category).find(opt => opt.value === tempFilters.subcategory) : null}
                      onChange={(selected) => setTempFilters(prev => ({
                        ...prev,
                        subcategory: selected ? selected.value : ''
                      }))}
                      menuPortalTarget={document.body}
                      styles={{
                        placeholder: (provided) => ({
                          ...provided,
                          color: 'black',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {tempFilters.subcategory && <label htmlFor="tier-select">Subcategory</label>}
                  </div>
                  <div className="floating-select-form" style={{ width: '100%' }}>
                    <Select
                      className={`walkin-form-dropdown ${tempFilters.priority ? "has-value" : ""}`}
                      classNamePrefix="ts-react-select"
                      options={priorityOptions}
                      placeholder="Priority"
                      isClearable
                      value={tempFilters.priority ? priorityOptions.find(opt => opt.value === tempFilters.priority) : null}
                      onChange={(selected) => setTempFilters(prev => ({
                        ...prev,
                        priority: selected ? selected.value : ''
                      }))}
                      menuPortalTarget={document.body}
                      styles={{
                        placeholder: (provided) => ({
                          ...provided,
                          color: 'black',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {tempFilters.priority && <label htmlFor="tier-select">Priority</label>}
                  </div>
                  <button
                    className="ts-mobile-filter-apply-button"
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        centre: tempFilters.centre,
                        category: tempFilters.category,
                        subcategory: tempFilters.subcategory,
                        priority: tempFilters.priority
                      }));
                      setShowMobileFilterPanel(false);
                    }}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className="ts-expanded-filters-grid">
              <Select
                className="ts-react-select-container"
                classNamePrefix="ts-react-select"
                options={centreOptions}
                placeholder="Centre"
                isClearable
                value={filters.centre ? centreOptions.find(opt => opt.value === filters.centre) : null}
                onChange={(selected) => handleFilterChange('centre', selected)}
                menuPortalTarget={document.body}
                styles={{
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'black',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <Select
                className="ts-react-select-container"
                classNamePrefix="ts-react-select"
                options={categoryOptions}
                placeholder="Category"
                isClearable
                value={filters.category ? categoryOptions.find(opt => opt.value === filters.category) : null}
                onChange={(selected) => handleFilterChange('category', selected)}
                menuPortalTarget={document.body}
                styles={{
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'black',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <Select
                className="ts-react-select-container"
                classNamePrefix="ts-react-select"
                options={getSubcategoryOptions()}
                placeholder="Subcategory"
                isClearable
                isDisabled={!filters.category}
                value={filters.subcategory ? getSubcategoryOptions().find(opt => opt.value === filters.subcategory) : null}
                onChange={(selected) => handleFilterChange('subcategory', selected)}
                menuPortalTarget={document.body}
                styles={{
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'black',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <Select
                className="ts-react-select-container"
                classNamePrefix="ts-react-select"
                options={priorityOptions}
                placeholder="Priority"
                isClearable
                value={filters.priority ? priorityOptions.find(opt => opt.value === filters.priority) : null}
                onChange={(selected) => handleFilterChange('priority', selected)}
                menuPortalTarget={document.body}
                styles={{
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'black',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          )}
        </div>

        {selectedTickets.length > 0 && hasAnyBulkPrivilege && (
          <div className="ts-bulk-actions-container">
            <span>{selectedTickets.length} selected</span>
            <Select
              className="ts-react-select-container"
              classNamePrefix="ts-react-select"
              options={bulkActionOptions}
              placeholder="Bulk Actions"
              isClearable
              value={
                bulkAction
                  ? bulkActionOptions
                    .flatMap(group => group.options || [group])
                    .find(opt => opt.value === bulkAction)
                  : null
              }
              onChange={handleBulkActionChange}
              styles={{
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            {showAssignDropdown && (
              <div style={{ display: 'flex' }}>
                <div style={{ display: 'flex', marginRight: '20px' }}>
                  <Select
                    className="ts-react-select-container"
                    classNamePrefix="ts-react-select"
                    options={users.map(user => ({ value: user.id, label: user.name }))}
                    placeholder="Select User(s)"
                    isClearable
                    isMulti
                    value={
                      selectedAssignee && selectedAssignee.length
                        ? selectedAssignee.map(sa => ({ value: sa.id, label: sa.name }))
                        : []
                    }
                    onChange={(selected) =>
                      setSelectedAssignee(
                        selected
                          ? selected.map(u => ({ id: u.value, name: u.label }))
                          : []
                      )
                    }
                    menuPortalTarget={document.body}
                    styles={{
                      placeholder: (provided) => ({
                        ...provided,
                        color: 'black',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />

                </div>
                <button
                  onClick={handleBulkAction}
                  className="ts-bulk-action-button"
                  disabled={!selectedAssignee.length}
                >
                  Apply
                </button>
              </div>
            )}
            <div>
              {bulkAction && bulkAction !== 'assign' && (
                <button onClick={handleBulkAction} className="ts-bulk-action-button">
                  Apply
                </button>
              )}
            </div>
          </div>
        )}

        <div className="ts-ticket-list-container ts-custom-scrollbar">
          {filteredTickets.length > 0 && (
            <div className="ts-pagination-summary">
              Showing {indexOfFirstTicket + 1} to {Math.min(indexOfLastTicket, filteredTickets.length)} of {filteredTickets.length} entries
            </div>
          )}
          {filteredTickets.length === 0 && (
            <div className="ts-ticket-list-empty">No tickets found</div>
          )}
          {currentTickets.map(ticket => (
            <div
              key={ticket.id}
              className={`ts-ticket-item ${selectedTicket?.id === ticket.id ? 'ts-ticket-item-selected' : ''}`}
              onClick={() => setSelectedTicket(ticket)}
            >

              <div className="ts-ticket-item-content">
                {hasPrivilege(userData, 'TICKET', 'TICKETS_BULK_ACTIONS') && (
                  <div>
                    {filters.category && filters.subcategory && (
                      <input
                        type="checkbox"
                        onClick={(e) => { e.stopPropagation(); }}
                        checked={selectedTickets.includes(ticket.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedTickets([...selectedTickets, ticket.id]);
                          } else {
                            setSelectedTickets(selectedTickets.filter(id => id !== ticket.id));
                          }
                        }}
                        className="ts-ticket-checkbox"
                      />
                    )}
                  </div>
                )}
                <div className="ts-ticket-text-area">
                  <h3 className="ts-ticket-title">{ticket.title}</h3>
                  <p className="ts-ticket-meta">{ticket.ticketId} • {ticket.centre}</p>
                  <div className="ts-ticket-tags-list">
                    <span className={`ts-ticket-tag-small ${getPriorityClass(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                    <span className={`ts-ticket-tag-small ${getStatusClass(ticket.status)}`}>
                      {getStatusIcon(ticket.status)} {ticket.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="ts-ticket-assignee-info">
                <UserAvatar name={ticket.createdBy} />
                <span className="ts-ticket-assignee-name">{ticket.assignedTo === userId ? 'You' : ticket.assignedTo}</span>
                <span className="ts-ticket-updated-at">{formatDate(ticket.updatedAt)}</span>
              </div>
            </div>
          ))}
        </div>


        {filteredTickets.length > ticketsPerPage && (
          <div className="ts-pagination-container">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="ts-pagination-button"
            >
              <ChevronLeft className="ts-icon-small" />
            </button>

            {(() => {
              const pageNumbers = [];
              const maxVisiblePages = 5;

              if (totalPages <= maxVisiblePages) {
                for (let i = 1; i <= totalPages; i++) {
                  pageNumbers.push(
                    <button
                      key={i}
                      onClick={() => paginate(i)}
                      className={`ts-pagination-button ${currentPage === i ? 'ts-pagination-active' : ''}`}
                    >
                      {i}
                    </button>
                  );
                }
              } else {
                pageNumbers.push(
                  <button
                    key={1}
                    onClick={() => paginate(1)}
                    className={`ts-pagination-button ${currentPage === 1 ? 'ts-pagination-active' : ''}`}
                  >
                    1
                  </button>
                );

                let startPage = Math.max(2, currentPage - 1);
                let endPage = Math.min(totalPages - 1, currentPage + 1);
                if (currentPage <= 3) {
                  endPage = 4;
                }

                if (currentPage >= totalPages - 2) {
                  startPage = totalPages - 3;
                }

                if (startPage > 2) {
                  pageNumbers.push(
                    <span key="start-ellipsis" className="ts-pagination-ellipsis">
                      ...
                    </span>
                  );
                }
                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <button
                      key={i}
                      onClick={() => paginate(i)}
                      className={`ts-pagination-button ${currentPage === i ? 'ts-pagination-active' : ''}`}
                    >
                      {i}
                    </button>
                  );
                }
                if (endPage < totalPages - 1) {
                  pageNumbers.push(
                    <span key="end-ellipsis" className="ts-pagination-ellipsis">
                      ...
                    </span>
                  );
                }
                pageNumbers.push(
                  <button
                    key={totalPages}
                    onClick={() => paginate(totalPages)}
                    className={`ts-pagination-button ${currentPage === totalPages ? 'ts-pagination-active' : ''}`}
                  >
                    {totalPages}
                  </button>
                );
              }

              return pageNumbers;
            })()}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="ts-pagination-button"
            >
              <ChevronRight className="ts-icon-small" />
            </button>
          </div>
        )}
      </aside>

      {window.innerWidth < 768 && selectedTicket && (
        <div className="ts-bottom-sheet">
          {loading && (
            <div className="premium-loader">
              <div className="progress-track">
                <div className="progress-bar"></div>
              </div>
              <div className="status-text">
                <span>L</span>
                <span>O</span>
                <span>A</span>
                <span>D</span>
                <span>I</span>
                <span>N</span>
                <span>G</span>
              </div>
            </div>
          )}
          <div ref={mobileDetailRef}>
            {selectedTicket ? (
              <>
                <TicketDetail
                  ticket={selectedTicket}
                  onClose={() => setSelectedTicket(null)}
                  setTickets={setTickets}
                  userId={userId}
                  getPriorityClass={getPriorityClass}
                  getStatusClass={getStatusClass}
                  getStatusIcon={getStatusIcon}
                  formatDate={formatDate}
                  updateTicketStatus={updateTicketStatus}
                  categories={categories}
                  refresh={fetchTickets}
                  users={users}
                  reassignTickets={reassignTickets}
                  updateTicketPriority={updateTicketPriority}
                  priorities={priorities}
                />
              </>
            ) : null}
          </div>
        </div>
      )}

      <main className="ts-main-content" style={{ flex: '0 55%' }}>
        {selectedTicket ? (
          <TicketDetail
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
            setTickets={setTickets}
            userId={userId}
            getPriorityClass={getPriorityClass}
            getStatusClass={getStatusClass}
            getStatusIcon={getStatusIcon}
            formatDate={formatDate}
            updateTicketStatus={updateTicketStatus}
            categories={categories}
            refresh={fetchTickets}
            users={users}
            reassignTickets={reassignTickets}
            updateTicketPriority={updateTicketPriority}
            priorities={priorities}
          />
        ) : (
          <div className="ts-empty-state">
            {loading && (
              <div className="premium-loader">
                <div className="progress-track">
                  <div className="progress-bar"></div>
                </div>
                <div className="status-text">
                  <span>L</span>
                  <span>O</span>
                  <span>A</span>
                  <span>D</span>
                  <span>I</span>
                  <span>N</span>
                  <span>G</span>
                </div>
              </div>
            )}
            <Info className="ts-empty-state-icon" />
            <h2 className="ts-empty-state-title">Select a Ticket to View Details</h2>
            <p className="ts-empty-state-text">Choose a ticket from the list on the left to see its full information, comments, and activity log. Or, create a new ticket to get started!</p>
            <button onClick={() => navigate('/newticket')} className="ts-sidebar-create-button">
              <Plus className="ts-icon-small" /> Create Ticket
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Ticket;